import React, { useMemo } from 'react';
import { Job, JobGauge as JobGaugeType, GaugeState, NumberGaugeComponent, BuffGaugeComponent } from '../types';

interface JobGaugeProps {
  job: Job;
  activeEffects: any;
}

const NumberComponent: React.FC<{ component: NumberGaugeComponent; activeEffects: any }> = ({ 
  component, 
  activeEffects 
}) => {
  const { max, displayType, color, countdown } = component;
  
  // 根据dataSource配置动态计算当前值
  let current = component.current;
  
  if (component.dataSource) {
    const { type, effectIds, property, fallback } = component.dataSource;
    
    switch (type) {
      case 'static':
        current = component.current;
        break;
        
      case 'effect_timer':
        // 从指定效果中获取剩余时间，支持多个效果ID（优先级顺序）
        if (effectIds && effectIds.length > 0) {
          const activeEffect = effectIds.map(id => activeEffects[id]).find(effect => effect);
          current = activeEffect ? activeEffect.remainingTime : (fallback ?? 0);
        }
        break;
        
      case 'effect_stacks':
        // 从指定效果中获取层数
        if (effectIds && effectIds.length > 0) {
          const activeEffect = effectIds.map(id => activeEffects[id]).find(effect => effect);
          current = activeEffect ? (activeEffect.stacks || 1) : (fallback ?? 0);
        }
        break;
        
      case 'effect':
        // 从指定效果中获取指定属性
        if (effectIds && effectIds.length > 0 && property) {
          const activeEffect = effectIds.map(id => activeEffects[id]).find(effect => effect);
          current = activeEffect ? (activeEffect[property] || (fallback ?? 0)) : (fallback ?? 0);
        }
        break;
        
      case 'effect_multi':
        // 多个效果的值相加
        if (effectIds && effectIds.length > 0) {
          current = effectIds.reduce((sum, id) => {
            const effect = activeEffects[id];
            if (effect && property) {
              return sum + (effect[property] || 0);
            }
            return sum;
          }, 0);
        }
        break;
        
      default:
        current = component.current;
    }
  }
  
  const percentage = max > 0 ? (current / max) * 100 : 0;

  return (
    <div className="gauge-number-component">
      {displayType !== 'text' && (
        <div 
          className="gauge-bar"
          style={{
            background: `linear-gradient(to right, ${color || '#FFD700'} ${percentage}%, #333 ${percentage}%)`,
            height: '8px',
            borderRadius: '4px',
            marginBottom: '4px'
          }}
        />
      )}
      {displayType !== 'bar' && (
        <div 
          className="gauge-text"
          style={{ 
            color: color || '#FFD700',
            fontSize: '14px',
            fontWeight: 'bold',
            textAlign: 'center'
          }}
        >
          {countdown?.enabled ? `${Math.ceil(current)}s` : `${current}/${max}`}
        </div>
      )}
    </div>
  );
};

const BuffComponent: React.FC<{ component: BuffGaugeComponent; activeEffects: any }> = ({ 
  component, 
  activeEffects 
}) => {
  const { statusId, mappings, defaultColor, displayMode } = component;
  
  // 单个图标模式的状态计算（始终计算，确保hook调用顺序一致）
  const activeStatus = useMemo(() => {
    if (!mappings) return null;
    
    return mappings.find(mapping => 
      activeEffects[mapping.statusId]
    );
  }, [mappings, activeEffects]);
  
  // 如果是红绿灯模式，显示所有映射的状态
  if (displayMode === 'traffic_light' && mappings) {
    return (
      <div className="gauge-buff-component" style={{ display: 'flex', gap: '4px' }}>
        {mappings.map((mapping, index) => {
          const isActive = activeEffects[mapping.statusId];
          const color = isActive ? mapping.color : '#666666'; // 激活显示对应颜色，未激活显示灰色
          const name = mapping.name || component.name;
          
          return (
            <div
              key={`${mapping.statusId}-${index}`}
              style={{
                width: '16px',
                height: '16px',
                backgroundColor: color,
                borderRadius: '50%', // 圆形，更像红绿灯
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '8px',
                fontWeight: 'bold',
                color: 'white',
                textShadow: '1px 1px 1px rgba(0,0,0,0.8)',
                border: '1px solid #333'
              }}
              title={name}
            >
            </div>
          );
        })}
      </div>
    );
  }

  // 单个图标模式
  // 确定显示的颜色和名称
  let currentColor = '#666666'; // 默认灰色
  let currentName = component.name;
  
  if (activeStatus) {
    currentColor = activeStatus.color;
    currentName = activeStatus.name || component.name;
  } else if (activeEffects[statusId]) {
    currentColor = defaultColor || '#8B4513';
    currentName = component.name;
  }

  return (
    <div className="gauge-buff-component">
      <div
        style={{
          width: '20px',
          height: '20px',
          backgroundColor: currentColor,
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '10px',
          fontWeight: 'bold',
          color: 'white',
          textShadow: '1px 1px 1px rgba(0,0,0,0.8)'
        }}
        title={currentName}
      >
      </div>
    </div>
  );
};

const GaugeStateRenderer: React.FC<{ gaugeState: GaugeState; activeEffects: any }> = ({ 
  gaugeState, 
  activeEffects 
}) => {
  return (
    <div className="gauge-state" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      {gaugeState.components.map((component, index) => (
        <div key={`${component.id}-${index}`} className={`gauge-${component.position}`}>
          {component.type === 'number' ? (
            <NumberComponent component={component} activeEffects={activeEffects} />
          ) : (
            <BuffComponent component={component} activeEffects={activeEffects} />
          )}
        </div>
      ))}
    </div>
  );
};

export const JobGauge: React.FC<JobGaugeProps> = ({ job, activeEffects }) => {
  const gauge = job.gauge as JobGaugeType;
  
  // 根据当前活跃的状态选择合适的量谱配置
  const activeGaugeState = useMemo(() => {
    if (!gauge || gauge.type !== 'dynamic') {
      return null;
    }
    
    // 首先查找有条件匹配的状态
    const conditionalState = gauge.states.find(state => {
      if (state.condition?.type === 'status' && state.condition.statusId) {
        const hasEffect = activeEffects[state.condition.statusId] && 
                          activeEffects[state.condition.statusId].remainingTime > 0;
        return hasEffect;
      }
      return false;
    });

    // 如果没有条件匹配的状态，使用默认状态
    return conditionalState || gauge.states.find(state => 
      state.condition?.type === 'default'
    );
  }, [gauge, activeEffects]);

  if (!activeGaugeState) {
    return null;
  }

  return (
    <div 
      className="job-gauge"
      style={{
        background: 'rgba(0, 0, 0, 0.7)',
        padding: '8px 12px',
        borderRadius: '6px',
        border: '1px solid #555',
        minWidth: '150px'
      }}
    >
      <div 
        style={{ 
          fontSize: '12px', 
          color: '#ccc', 
          marginBottom: '8px', 
          textAlign: 'center' 
        }}
      >
        {activeGaugeState.name}
      </div>
      <GaugeStateRenderer gaugeState={activeGaugeState} activeEffects={activeEffects} />
    </div>
  );
};